class FacetFiltersForm extends HTMLElement {
    constructor() {
        super();
        this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

        // Short enough to feel immediate on a click, long enough to group a burst of taps
        // into a single request when someone ticks several sizes in a row.
        this.debouncedOnSubmit = debounce((event) => {
            this.onSubmitHandler(event);
        }, 150);

        const facetForm = this.querySelector('form');
        // Price inputs submit on change (blur or Enter) so typing "150" does not fire a request
        // for "1" and another for "15", and so the min/max clamp runs before the values are read.
        facetForm.addEventListener('input', (event) => {
            if (event.target.closest('price-range')) {
                // Typing a price does not submit yet, so the apply count is stale until it does.
                // Comparing against defaultValue (the applied one) also clears the state when the
                // shopper undoes what they typed.
                const isStale = [...facetForm.querySelectorAll('price-range input')].some(
                    (input) => input.value !== input.defaultValue,
                );
                document.getElementById('closeFilterOverlay')?.classList.toggle('is-pending', isStale);
                return;
            }
            this.debouncedOnSubmit(event);
        });
        facetForm.addEventListener('change', (event) => {
            if (!event.target.closest('price-range')) return;
            this.debouncedOnSubmit(event);
        });

        const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
        if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
    }

    static setListeners() {
        const onHistoryChange = (event) => {
            const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
            if (searchParams === FacetFiltersForm.searchParamsPrev) return;
            FacetFiltersForm.renderPage(searchParams, null, false);
        };
        window.addEventListener('popstate', onHistoryChange);
    }

    static toggleActiveFacets(disable = true) {
        document.querySelectorAll('.js-facet-remove').forEach((element) => {
            element.classList.toggle('disabled', disable);
        });
    }

    // Single place that turns the filtering state on and off, so the progress bar, the dimmed
    // grid and the apply button never disagree with each other.
    static toggleFilteringState(isFiltering) {
        document.querySelector('.facets-container')?.classList.toggle('is-filtering', isFiltering);
        const applyButton = document.getElementById('closeFilterOverlay');
        applyButton?.classList.toggle('is-busy', isFiltering);
        if (!isFiltering) applyButton?.classList.remove('is-pending');
    }

    static renderPage(searchParams, event, updateURLHash = true) {
        FacetFiltersForm.searchParamsPrev = searchParams;
        const sections = FacetFiltersForm.getSections();
        const countContainer = document.getElementById('ProductCount');
        const countContainerDesktop = document.getElementById('ProductCountDesktop');
        const loadingSpinners = document.querySelectorAll(
            '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
        );
        loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
        FacetFiltersForm.toggleFilteringState(true);
        document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
        if (countContainer) {
            countContainer.classList.add('loading');
        }
        if (countContainerDesktop) {
            countContainerDesktop.classList.add('loading');
        }

        sections.forEach((section) => {
            const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
            const filterDataUrl = (element) => element.url === url;

            FacetFiltersForm.filterData.some(filterDataUrl)
                ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
                : FacetFiltersForm.renderSectionFromFetch(url, event);
        });

        if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
    }

    static renderSectionFromFetch(url, event) {
        fetch(url)
            .then((response) => response.text())
            .then((responseText) => {
                const html = responseText;
                FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
                FacetFiltersForm.renderFilters(html, event);
                FacetFiltersForm.renderProductGridContainer(html);
                FacetFiltersForm.renderProductCount(html);
                if (typeof initializeScrollAnimationTrigger === 'function')
                    initializeScrollAnimationTrigger(html.innerHTML);
            })
            .catch(() => {
                // Never leave the grid dimmed and unclickable if the request fails
                FacetFiltersForm.revealProductGrid();
                FacetFiltersForm.toggleFilteringState(false);
                document.querySelectorAll('.loading__spinner').forEach((spinner) => spinner.classList.add('hidden'));
                document.getElementById('ProductGridContainer')?.querySelector('.collection')?.classList.remove('loading');
                document.getElementById('ProductCount')?.classList.remove('loading');
            });
    }

    static renderSectionFromCache(filterDataUrl, event) {
        const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
    }

    static renderProductGridContainer(html) {
        document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
            .parseFromString(html, 'text/html')
            .getElementById('ProductGridContainer').innerHTML;

        document
            .getElementById('ProductGridContainer')
            .querySelectorAll('.scroll-trigger')
            .forEach((element) => {
                element.classList.add('scroll-trigger--cancel');
            });

        FacetFiltersForm.revealProductGrid();

        // Fade the new results in instead of swapping them in place
        const collection = document.getElementById('ProductGridContainer').querySelector('.collection');
        if (collection) {
            collection.classList.add('is-entering');
            requestAnimationFrame(() => {
                requestAnimationFrame(() => collection.classList.remove('is-entering'));
            });
        }
    }

    static renderProductCount(html) {
        const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
        const count = parsedHTML.getElementById('ProductCount').innerHTML;
        const container = document.getElementById('ProductCount');

        // Keep the count on the mobile apply button in sync: inside the drawer it is the only
        // place where the shopper can see how many results are left.
        const applyCountSource = parsedHTML.querySelector('[data-apply-count]');
        const applyCountTarget = document.querySelector('[data-apply-count]');
        if (applyCountSource && applyCountTarget) applyCountTarget.innerHTML = applyCountSource.innerHTML;
        FacetFiltersForm.toggleFilteringState(false);

        const containerDesktop = document.getElementById('ProductCountDesktop');
        container.innerHTML = count;
        container.classList.remove('loading');
        if (containerDesktop) {
            containerDesktop.innerHTML = count;
            containerDesktop.classList.remove('loading');
        }
        const loadingSpinners = document.querySelectorAll(
            '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
        );
        loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
    }

    static renderFilters(html, event) {
        const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
        const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
            '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
        );
        const facetDetailsElementsFromDom = document.querySelectorAll(
            '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
        );

        // Remove facets that are no longer returned from the server
        Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
            if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
                currentElement.remove();
            }
        });

        const matchesId = (element) => {
            const jsFilter = event ? event.target.closest('.js-filter') : undefined;
            return jsFilter ? element.id === jsFilter.id : false;
        };

        const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
        const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

        facetsToRender.forEach((elementToRender, index) => {
            const currentElement = document.getElementById(elementToRender.id);
            // Element already rendered in the DOM so just update the innerHTML
            if (currentElement) {
                document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
            } else {
                if (index > 0) {
                    const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
                    // Same facet type (eg horizontal/vertical or drawer/mobile)
                    if (elementToRender.className === previousElementClassName) {
                        document.getElementById(previousElementId).after(elementToRender);
                        return;
                    }
                }

                if (elementToRender.parentElement) {
                    document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
                }
            }
        });

        FacetFiltersForm.renderActiveFacets(parsedHTML);
        FacetFiltersForm.renderAdditionalElements(parsedHTML);

        if (countsToRender) {
            const closestJSFilterID = event.target.closest('.js-filter').id;

            if (closestJSFilterID) {
                FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
                FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

                const newFacetDetailsElement = document.getElementById(closestJSFilterID);
                const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
                    ? `.mobile-facets__close-button`
                    : `.facets__summary`;
                const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

                const isTextInput = event.target.getAttribute('type') === 'text';

                if (newElementToActivate && !isTextInput) newElementToActivate.focus();
            }
        }
    }

    static renderActiveFacets(html) {
        const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

        activeFacetElementSelectors.forEach((selector) => {
            const activeFacetsElement = html.querySelector(selector);
            if (!activeFacetsElement) return;
            document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
        });

        FacetFiltersForm.toggleActiveFacets(false);
    }

    static renderAdditionalElements(html) {
        const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

        mobileElementSelectors.forEach((selector) => {
            if (!html.querySelector(selector)) return;
            document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
        });

        document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
    }

    static renderCounts(source, target) {
        const targetSummary = target.querySelector('.facets__summary');
        const sourceSummary = source.querySelector('.facets__summary');

        if (sourceSummary && targetSummary) {
            targetSummary.outerHTML = sourceSummary.outerHTML;
        }

        const targetHeaderElement = target.querySelector('.facets__header');
        const sourceHeaderElement = source.querySelector('.facets__header');

        if (sourceHeaderElement && targetHeaderElement) {
            targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
        }

        const targetWrapElement = target.querySelector('.facets-wrap');
        const sourceWrapElement = source.querySelector('.facets-wrap');

        if (sourceWrapElement && targetWrapElement) {
            const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
            if (isShowingMore) {
                sourceWrapElement
                    .querySelectorAll('.facets__item.hidden')
                    .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
            }

            targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
        }
    }

    static renderMobileCounts(source, target) {
        const targetFacetsList = target.querySelector('.mobile-facets__list');
        const sourceFacetsList = source.querySelector('.mobile-facets__list');

        if (sourceFacetsList && targetFacetsList) {
            targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
        }
    }

    static updateURLHash(searchParams) {
        history.pushState(
            { searchParams },
            '',
            `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`,
        );
    }

    // A bare collection URL stays clean for search engines, so the in-stock default is applied
    // here instead of by redirecting: the section flags the grid when the collection holds
    // sold-out products and the in-stock grid is fetched and swapped in without touching the URL.
    static applyDefaultStockFilter() {
        const container = document.getElementById('ProductGridContainer');
        if (!container?.hasAttribute('data-stock-filter-pending')) return;

        const params = new URLSearchParams(window.location.search);
        params.set('filter.v.availability', '1');
        const searchParams = params.toString();

        // Going back to the landing entry must restore the in-stock grid, not the bare one
        FacetFiltersForm.searchParamsInitial = searchParams;
        setTimeout(FacetFiltersForm.revealProductGrid, FacetFiltersForm.STOCK_FILTER_REVEAL_TIMEOUT_MS);
        FacetFiltersForm.renderPage(searchParams, null, false);
    }

    static revealProductGrid() {
        document.getElementById('ProductGridContainer')?.removeAttribute('data-stock-filter-pending');
    }

    static getSections() {
        return [
            {
                section: document.getElementById('product-grid').dataset.id,
            },
        ];
    }

    createSearchParams(form) {
        const formData = new FormData(form);
        const params = new URLSearchParams(formData);
        params.set('filter.v.availability', '1');
        return params.toString();
    }

    onSubmitForm(searchParams, event) {
        FacetFiltersForm.renderPage(searchParams, event);
    }

    onSubmitHandler(event) {
        event.preventDefault();
        const sortFilterForms = document.querySelectorAll('facet-filters-form form');
        if (event.srcElement.className == 'mobile-facets__checkbox') {
            const searchParams = this.createSearchParams(event.target.closest('form'));
            this.onSubmitForm(searchParams, event);
        } else {
            const forms = [];
            const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

            sortFilterForms.forEach((form) => {
                if (!isMobile) {
                    if (
                        form.id === 'FacetSortForm' ||
                        form.id === 'FacetFiltersForm' ||
                        form.id === 'FacetSortDrawerForm'
                    ) {
                        forms.push(this.createSearchParams(form));
                    }
                } else if (form.id === 'FacetFiltersFormMobile') {
                    forms.push(this.createSearchParams(form));
                }
            });
            this.onSubmitForm(forms.join('&'), event);
        }
    }

    onActiveFilterClick(event) {
        event.preventDefault();
        FacetFiltersForm.toggleActiveFacets();
        let url =
            event.currentTarget.href.indexOf('?') == -1
                ? ''
                : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
        const params = new URLSearchParams(url);
        params.set('filter.v.availability', '1');
        FacetFiltersForm.renderPage(params.toString());
    }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
// Upper bound for the hidden grid if the in-stock request hangs: the bare grid shows instead
FacetFiltersForm.STOCK_FILTER_REVEAL_TIMEOUT_MS = 8000;
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
FacetFiltersForm.applyDefaultStockFilter();

class PriceRange extends HTMLElement {
    constructor() {
        super();
        this.querySelectorAll('input').forEach((element) => {
            element.addEventListener('change', this.onRangeChange.bind(this));
            element.addEventListener('keydown', this.onKeyDown.bind(this));
        });
        this.setMinAndMaxValues();
    }

    onRangeChange(event) {
        this.adjustToValidValues(event.currentTarget);
        this.setMinAndMaxValues();
    }

    onKeyDown(event) {
        if (event.metaKey) return;

        const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
        if (!event.key.match(pattern)) event.preventDefault();
    }

    setMinAndMaxValues() {
        const inputs = this.querySelectorAll('input');
        const minInput = inputs[0];
        const maxInput = inputs[1];
        if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
        if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
        if (minInput.value === '') maxInput.setAttribute('data-min', 0);
        if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
    }

    adjustToValidValues(input) {
        const value = Number(input.value);
        const min = Number(input.getAttribute('data-min'));
        const max = Number(input.getAttribute('data-max'));

        if (value < min) input.value = min;
        if (value > max) input.value = max;
    }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
    constructor() {
        super();
        const facetLink = this.querySelector('a');
        facetLink.setAttribute('role', 'button');
        facetLink.addEventListener('click', this.closeFilter.bind(this));
        facetLink.addEventListener('keyup', (event) => {
            event.preventDefault();
            if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
        });
    }

    closeFilter(event) {
        event.preventDefault();
        // Immediate acknowledgement: the pill fades while its request is in flight
        this.classList.add('is-removing');
        const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
        form.onActiveFilterClick(event);
    }
}

customElements.define('facet-remove', FacetRemove);
